import * as THREE from 'three';

/**
 * Sun position — the Shadows panel's solar model.
 *
 * Compact solar-position approximation (declination + hour angle at a fixed
 * latitude): good enough to drive a believable shadow arc through the day
 * and the seasons, matching Sketch's Time/Date slider behavior. Z-up world.
 */

export interface SunPosition {
    /** Radians above the horizon (can be negative at night). */
    elevation: number;
    /** Radians clockwise from north (compass convention). */
    azimuth: number;
    /** Unit vector pointing FROM the scene TOWARD the sun. */
    direction: THREE.Vector3;
}

const DEFAULT_LATITUDE_DEG = 45;

const clamp = (x: number, lo: number, hi: number): number => Math.min(hi, Math.max(lo, x));

export function sunDirection(
    dayOfYear: number,
    hour: number,
    latitudeDeg: number = DEFAULT_LATITUDE_DEG
): SunPosition {
    const declination = THREE.MathUtils.degToRad(
        23.44 * Math.sin(((2 * Math.PI) / 365) * (dayOfYear - 81))
    );
    const latitude = THREE.MathUtils.degToRad(latitudeDeg);
    const hourAngle = THREE.MathUtils.degToRad(15 * (hour - 12));

    const sinElevation =
        Math.sin(declination) * Math.sin(latitude)
        + Math.cos(declination) * Math.cos(latitude) * Math.cos(hourAngle);
    const elevation = Math.asin(clamp(sinElevation, -1, 1));

    const azimuth = Math.atan2(
        Math.sin(hourAngle),
        Math.cos(hourAngle) * Math.sin(latitude) - Math.tan(declination) * Math.cos(latitude)
    );

    // Compass azimuth (from north, clockwise) → world Z-up vector: north = +Y,
    // east = +X. direction points from the scene toward the sun.
    const cosElevation = Math.max(Math.cos(elevation), 0);
    const direction = new THREE.Vector3(
        cosElevation * Math.sin(azimuth),
        cosElevation * Math.cos(azimuth),
        Math.sin(elevation)
    ).normalize();

    return { elevation, azimuth, direction };
}
